<script setup>
import { deleteRequest, exportFile, getRequest } from "@/utils/request.js";
import {
  ExportOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import UserFormModal from "./modules/UserFormModal.vue";
import ResetPasswordModal from "@/views/system/user/modules/ResetPasswordModal.vue";

const resetPasswordModalRef = ref();
const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const columns = [
  {
    key: "username",
    dataIndex: "username",
    title: "账户名",
    width: "15%",
  },
  {
    key: "realName",
    dataIndex: "realName",
    title: "姓名",
    width: "15%",
  },
  {
    key: "phone",
    dataIndex: "phone",
    title: "电话",
    width: "15%",
  },
  {
    key: "roleNames",
    dataIndex: "roleNames",
    title: "角色",
    width: "15%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "状态",
    width: "15%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/user", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = () => {
  modalRef.value.title = "用户新增";
  modalRef.value.handleAdd();
};

const handleEdit = (record) => {
  modalRef.value.title = "用户编辑";
  modalRef.value.handleEdit(record);
};

const handleDelete = (record) => {
  deleteRequest(`/user/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const exportExcel = () => {
  const requestBody = Object.assign({}, unref(searchFormModel));
  exportFile("/user/export", requestBody, "用户导出.xlsx");
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const handleResetPassword = (record) => {
  resetPasswordModalRef.value.handleShow({
    id: record.id,
    username: record.username,
    realName: record.realName,
  });
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :label-col="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="用户名" name="username">
              <a-input
                v-model:value="searchFormModel.username"
                placeholder="请输入用户名"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="姓名" name="realName">
              <a-input
                v-model:value="searchFormModel.realName"
                placeholder="请输入姓名"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="手机号" name="phone">
              <a-input
                v-model:value="searchFormModel.phone"
                placeholder="请输入手机号"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
            >
              重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button
          v-has="'user:add'"
          type="primary"
          style="margin-right: 18px"
          :icon="h(PlusOutlined)"
          @click="handleAdd"
        >
          新增
        </a-button>
        <a-button type="primary" @click="exportExcel" :icon="h(ExportOutlined)">
          导出
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green"> 启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red"> 停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a v-has="'user:update'" type="link" @click="handleEdit(record)"
              >编辑</a
            >
            <a-divider type="vertical" />
            <a-popconfirm
              v-has="'user:delete'"
              title="确认要删除吗?"
              @confirm="handleDelete(record)"
            >
              <a v-has="'user:delete'" type="link">删除</a>
            </a-popconfirm>
            <a-divider type="vertical" />
            <a v-has="'user:update'" v-if="record['status']" type="link"
              >停用</a
            >
            <a v-has="'user:update'" v-else type="link">启用</a>
            <a-divider type="vertical" />
            <a
              v-has="'user:resetPassword'"
              type="link"
              @click="handleResetPassword(record)"
              >重置密码</a
            >
          </template>
        </template>
      </a-table>
    </a-card>
    <user-form-modal ref="modalRef" @ok="loadData" />
    <reset-password-modal ref="resetPasswordModalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
